import {
    PayloadAction,
    createAsyncThunk,
    createEntityAdapter,
    createSlice,
} from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import * as biometricTransactionService from '../../service/biometric-transaction.service';
import {
    BiometricTransaction,
    BiometricTransactionSearchCriteria,
} from '../../service/model/biometric.model';
import { PaginatedResponse } from '../../service/model/paginated-response';

export interface BiometricTransactionState {
    isLoading: boolean;
    currentPage: number;
    totalPages: number;
    totalRecords: number;
}

const biometricTransactionsAdapter = createEntityAdapter<BiometricTransaction>({
    sortComparer: (a, b) =>
        new Date(b.punchTime).getTime() - new Date(a.punchTime).getTime(),
});

export const initialState = biometricTransactionsAdapter.getInitialState({
    isLoading: false,
    currentPage: 0,
    totalPages: 0,
    totalRecords: 0,
} as BiometricTransactionState);

export const fetchBiometricDeviceTransactionsPage = createAsyncThunk<
    PaginatedResponse<BiometricTransaction>,
    BiometricTransactionSearchCriteria
>('biometricTransactions/page', async (search, { rejectWithValue }) => {
    return biometricTransactionService
        .getBiometricDeviceTransactionPage(search)
        .catch((errors) => {
            return rejectWithValue(errors);
        });
});

const biometricTransactionSlice = createSlice({
    name: 'biometricTransactions',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchBiometricDeviceTransactionsPage.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                fetchBiometricDeviceTransactionsPage.fulfilled,
                (
                    state,
                    action: PayloadAction<
                        PaginatedResponse<BiometricTransaction>
                    >,
                ) => {
                    biometricTransactionsAdapter.setAll(
                        state,
                        action.payload.data,
                    );
                    state.currentPage = action.payload.currentPage;
                    state.totalPages = action.payload.totalPages;
                    state.totalRecords = action.payload.totalRecords;
                    state.isLoading = false;
                },
            )
            .addCase(fetchBiometricDeviceTransactionsPage.rejected, (state) => {
                state.isLoading = false;
            });
    },
});

export const {
    selectAll: selectBiometricTransactions,
    selectById: selectBiometricTransactionById,
} = biometricTransactionsAdapter.getSelectors<RootState>(
    (state) => state.biometricTransactions,
);

export const selectIsLoading = (state: RootState) =>
    state.biometricTransactions?.isLoading;
export const selectCurrentPage = (state: RootState) =>
    state.biometricTransactions.currentPage;
export const selectTotalPages = (state: RootState) =>
    state.biometricTransactions.totalPages;
export const selectTotalRecords = (state: RootState) =>
    state.biometricTransactions.totalRecords;

export const biometricTransactionActions = biometricTransactionSlice.actions;

export default biometricTransactionSlice.reducer;
